import React, { createRef, useState, useEffect, useCallback } from 'react'
import { Box, Typography, Icon, IconButton } from '@mui/material'
import { Field, ErrorMessage } from 'formik'
import AdxErrorMessage from 'components/custom/AdxErrorMessage'
import { CSVLink } from 'react-csv'
import AdxDropzone from 'components/reusable/AdxDropzone'
import { _calculateFileSize } from 'services/helper/FileSize'
import AdxButton from 'components/custom/AdxButton'

const ACMatchingCsvUpload = ({ formikProps, handleConfirm, completeUploadMatching, setUpload }) => {
  const { values, setFieldValue, handleSubmit, resetForm } = formikProps
  const [uploadState, setUploadState] = useState('PENDING')
  const [uploadProgress, setUploadProgress] = useState(0)
  const [files, setFiles] = useState([])
  const [errFiles, setErrFiles] = useState([])
  const dropzoneRef = createRef()

  const style = {
    color: '#09A3A9',
    cursor: 'pointer',
    textDecoration: 'underline',
  }
  const CSVHeader = [['reviewee_email', 'reviewee_name', 'reviewer_email', 'reviewer_name']]

  useEffect(() => {
    if (values.import_participant_file) {
      handleSubmit()
    }
  }, [values]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (values.import_participant_file && values.import_participant_file.length !== 0) {
      setUploadState('READY')
      setFiles(values.import_participant_file)
    }
  }, [values.import_participant_file])

  useEffect(() => {
    if (!completeUploadMatching) handleCancelUpload()
  }, [completeUploadMatching]) // eslint-disable-line react-hooks/exhaustive-deps

  const openDialog = () => {
    if (dropzoneRef.current) {
      dropzoneRef.current.open()
    }
  }

  const handleUpload = useCallback(async (acceptedFiles, fileRejections) => {
    setUploadState('UPLOADING')
    if (fileRejections.length && !acceptedFiles.length) {
      setUploadState('NOT_READY')
      setFiles([])
      setErrFiles(fileRejections.map((file) => file))
    }
    let csvData = acceptedFiles.map((file) =>
      Object.assign(file, {
        preview: URL.createObjectURL(file),
      })
    )
    setFieldValue('import_participant_file', csvData)
    setFiles(csvData)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const handleCancelUpload = () => {
    setUploadState('PENDING')
    setUploadProgress(0)
    setFiles([])
    setErrFiles([])
    resetForm()
    handleConfirm(false)
    setUpload(false)
  }
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        rowGap: 1,
      }}
    >
      <Box sx={{ width: 1, display: 'flex', alignItems: 'center', columnGap: 5 }}>
        <Typography variant="body1" color="light-gray-text.main">
          Upload a list of reviewer and reviewee via CSV file. One evaluation will has only one
          matching CSV file. Data that has error will not be uploaded.{' '}
          <CSVLink
            filename={'Upload Custom Participants.csv'}
            data={CSVHeader}
            style={{ textDecoration: 'none' }}
          >
            <span style={style}>Download template</span>
          </CSVLink>
        </Typography>
        <Box>
          <Field
            name="import_participant_file"
            values={values.import_participant_file}
            type="hidden"
          />
          <Box display="flex" flexDirection="column" rowGap={1}>
            <AdxButton
              variant={'outlined'}
              onClick={openDialog}
              sx={{
                width: '150px',
              }}
            >
              <Icon
                sx={{
                  color: 'alternate.main',
                }}
              >
                publish
              </Icon>
              Upload CSV
            </AdxButton>
            <AdxDropzone
              setUploadState={setUploadState}
              ref={dropzoneRef}
              useDropzoneObject={{
                accept: [
                  '.csv',
                  'application/vnd.ms-excel',
                  'text/csv',
                  '.xlsx',
                  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                ],
                maxSize: 2097152,
                maxFiles: 1,
                onDrop: handleUpload,
              }}
              sx={{
                display: 'none',
              }}
            />
          </Box>
        </Box>
      </Box>
      {uploadState !== 'PENDING' && (
        <>
          <Box
            sx={{
              minWidth: '250px',
              height: '100%',
              width: '100%',
              borderRadius: '4.5px',
              border: '3px solid',
              borderColor: 'gray-line.main',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {/* Box footer (white area) */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
                p: 2,
              }}
            >
              {uploadState === 'NOT_READY'
                ? errFiles.map(({ file, errors }, index) => (
                    <>
                      <Box
                        display="flex"
                        flexDirection="row"
                        alignItems="center"
                        columnGap={1}
                        key={index}
                      >
                        <Icon sx={{ color: 'error.main' }}>cancel</Icon>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {file.name} not ready
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="error.main" key={index}>
                        {errors[0].message}
                      </Typography>
                      <IconButton onClick={handleCancelUpload}>
                        <Icon sx={{ color: 'error.main' }}>do_disturb_on</Icon>
                      </IconButton>
                    </>
                  ))
                : files.map((file, index) => (
                    <Box display="flex" flexDirection="column" rowGap={1} key={index}>
                      <Box display="flex" columnGap={1}>
                        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                          {file.name}
                        </Typography>
                        <Typography variant="body2">{_calculateFileSize(file.size)}</Typography>
                      </Box>
                      {(
                        <ErrorMessage name="import_participant_file" component={AdxErrorMessage} />
                      ) && (
                        <Typography variant="body2">
                          {
                            <ErrorMessage
                              name="import_participant_file"
                              component={AdxErrorMessage}
                            />
                          }
                        </Typography>
                      )}
                    </Box>
                  ))}

              {uploadState === 'UPLOADING' && (
                <Box display="flex" alignItems="center" columnGap={1}>
                  <Typography variant="body1" color="light-gray-text.main">
                    {`Uploading (${uploadProgress.toFixed(0)}%)`}
                  </Typography>
                  <IconButton onClick={handleCancelUpload}>
                    <Icon sx={{ color: 'error.main' }}>do_disturb_on</Icon>
                  </IconButton>
                </Box>
              )}

              {uploadState === 'READY' && (
                <Box display="flex" alignItems="center" columnGap={1}>
                  <AdxButton variant={'ghost'} size="small" onClick={handleCancelUpload}>
                    Replace
                  </AdxButton>
                  <IconButton onClick={handleCancelUpload}>
                    <Icon sx={{ color: 'error.main' }}>do_disturb_on</Icon>
                  </IconButton>
                </Box>
              )}
            </Box>
          </Box>
        </>
      )}
    </Box>
  )
}

export default ACMatchingCsvUpload
